import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Send, Lock, Image, Video, Hash, MessageCircle, Plus } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ChatPage() {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [threads, setThreads] = useState([]);
  const [activeThread, setActiveThread] = useState('global');
  const [messages, setMessages] = useState([]);
  const [newMsg, setNewMsg] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [showThreads, setShowThreads] = useState(true);
  const messagesEndRef = useRef(null);

  const fetchThreads = useCallback(async () => {
    if (!token) return;
    try {
      const res = await axios.get(`${API}/chat/threads`, { headers: { Authorization: `Bearer ${token}` } });
      setThreads(res.data.threads);
    } catch (err) { console.error(err); }
  }, [token]);

  const fetchMessages = useCallback(async () => {
    setLoading(true);
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await axios.get(`${API}/chat/${activeThread}/messages`, { headers });
      setMessages(res.data.messages);
    } catch (err) { console.error(err); }
    setLoading(false);
  }, [activeThread, token]);

  useEffect(() => { fetchThreads(); }, [fetchThreads]);
  useEffect(() => { fetchMessages(); }, [fetchMessages]);
  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  // Auto refresh messages every 5s
  useEffect(() => {
    const interval = setInterval(fetchMessages, 5000);
    return () => clearInterval(interval);
  }, [fetchMessages]);

  const handleSend = async (e) => {
    e.preventDefault();
    if (!newMsg.trim() || !token) { if (!token) navigate('/auth'); return; }
    setSending(true);
    try {
      await axios.post(`${API}/chat/${activeThread}/messages`, 
        { content: newMsg },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setNewMsg('');
      fetchMessages();
    } catch (err) { console.error(err); }
    setSending(false);
  };

  return (
    <div className="h-[calc(100vh-64px)] flex bg-texture" data-testid="chat-page">
      {/* Thread Sidebar */}
      <div className={`${showThreads ? 'w-72' : 'w-0'} border-r border-[--ch-border] bg-[--ch-bg-card] transition-all overflow-hidden flex-shrink-0`} data-testid="thread-sidebar">
        <div className="p-4 border-b border-[--ch-border]">
          <h2 className="text-sm font-bold flex items-center gap-2">
            <MessageCircle size={16} className="text-[--ch-cyan]" />
            Threads
          </h2>
        </div>
        <div className="overflow-y-auto h-full pb-20">
          {/* Global thread */}
          <button
            data-testid="thread-global"
            onClick={() => setActiveThread('global')}
            className={`w-full text-left p-4 border-b border-[--ch-border] transition-colors ${
              activeThread === 'global' ? 'bg-[rgba(0,240,255,0.05)] border-l-2 border-l-[--ch-cyan]' : 'hover:bg-[--ch-bg-card-hover]'
            }`}
          >
            <div className="flex items-center gap-2">
              <Hash size={14} className="text-[--ch-cyan]" />
              <span className="text-sm font-semibold">Global Chat</span>
            </div>
            <p className="text-xs text-[--ch-text-secondary] mt-1 truncate">Public community thread</p>
          </button>
          
          {/* Other threads */}
          {threads.filter(t => t.thread_id !== 'global').map(thread => (
            <button
              key={thread.thread_id}
              data-testid={`thread-${thread.thread_id}`}
              onClick={() => setActiveThread(thread.thread_id)}
              className={`w-full text-left p-4 border-b border-[--ch-border] transition-colors ${
                activeThread === thread.thread_id ? 'bg-[rgba(0,240,255,0.05)] border-l-2 border-l-[--ch-cyan]' : 'hover:bg-[--ch-bg-card-hover]'
              }`}
            >
              <p className="text-sm font-semibold truncate">{thread.thread_id}</p>
              <p className="text-xs text-[--ch-text-secondary] mt-1 truncate">{thread.last_message}</p>
              <p className="text-[10px] text-[--ch-text-secondary] mt-1">{thread.message_count} messages</p>
            </button>
          ))}
        </div>
      </div>

      {/* Chat Area */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Chat Header */}
        <div className="h-14 border-b border-[--ch-border] flex items-center px-4 gap-3 bg-[--ch-bg-card] flex-shrink-0">
          <button
            data-testid="toggle-threads-btn"
            onClick={() => setShowThreads(!showThreads)}
            className="text-[--ch-text-secondary] hover:text-white transition-colors lg:hidden"
          >
            <MessageCircle size={18} />
          </button>
          <Hash size={16} className="text-[--ch-cyan]" />
          <h3 className="text-sm font-bold">{activeThread === 'global' ? 'Global Chat' : activeThread}</h3>
          <span className="text-xs text-[--ch-text-secondary]">{messages.length} messages</span>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4" data-testid="messages-container">
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <div className="w-8 h-8 border-2 border-[--ch-cyan] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : messages.length === 0 ? (
            <div className="flex items-center justify-center h-full text-[--ch-text-secondary]">
              <div className="text-center">
                <MessageCircle size={40} className="mx-auto mb-3 opacity-30" />
                <p className="text-sm">No messages yet. Start the conversation!</p>
              </div>
            </div>
          ) : (
            messages.map(msg => (
              <div key={msg.id} className="flex gap-3 animate-fade-in-up" data-testid={`message-${msg.id}`}>
                <div className="w-8 h-8 rounded-full bg-[--ch-bg-secondary] flex items-center justify-center flex-shrink-0 text-xs font-bold text-[--ch-cyan] border border-[--ch-border]">
                  {msg.username?.[0]?.toUpperCase() || '?'}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className={`text-sm font-bold ${msg.role === 'creator' ? 'creator-badge' : ''}`}>
                      {msg.username}
                    </span>
                    {msg.role === 'creator' && (
                      <span className="text-[10px] bg-[rgba(0,240,255,0.1)] text-[--ch-cyan] px-1.5 py-0.5 rounded font-bold">CREATOR</span>
                    )}
                    <span className="text-[10px] text-[--ch-text-secondary]">
                      {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </span>
                  </div>
                  <p className="text-sm text-[--ch-text-secondary] mt-0.5 break-words">{msg.content}</p>
                  {msg.media_url && msg.has_access && (
                    <div className="mt-2 max-w-xs rounded-lg overflow-hidden border border-[--ch-border]">
                      {msg.media_type === 'video' ? (
                        <video src={msg.media_url} controls className="w-full" />
                      ) : (
                        <img src={msg.media_url} alt="" className="w-full" />
                      )}
                    </div>
                  )}
                  {msg.is_paygated && !msg.has_access && (
                    <div className="mt-2 p-4 rounded-lg border border-[--ch-magenta] bg-[rgba(255,0,229,0.05)] flex items-center gap-3">
                      <Lock size={18} className="text-[--ch-magenta]" />
                      <div>
                        <p className="text-sm font-bold text-[--ch-magenta]">${msg.paygate_price?.toFixed(2)} to unlock</p>
                        <p className="text-xs text-[--ch-text-secondary]">Premium content</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <form onSubmit={handleSend} className="border-t border-[--ch-border] p-4 bg-[--ch-bg-card] flex-shrink-0" data-testid="chat-input-form">
          <div className="flex gap-2">
            <input
              data-testid="chat-message-input"
              type="text"
              value={newMsg}
              onChange={e => setNewMsg(e.target.value)}
              placeholder={token ? "Type a message..." : "Sign in to chat"}
              disabled={!token}
              className="flex-1 bg-[--ch-bg-secondary] border border-[--ch-border] rounded-lg px-4 py-2.5 text-sm text-white focus:border-[--ch-cyan] focus:outline-none transition-colors disabled:opacity-50"
            />
            <button
              data-testid="chat-send-btn"
              type="submit"
              disabled={!token || sending || !newMsg.trim()}
              className="btn-neon px-4 py-2.5 rounded-lg disabled:opacity-50 flex items-center gap-1"
            >
              <Send size={16} />
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
